import { Approval } from '@shared/modules/approvals/approvals.types';
import {
  TaskModuleRelatedToType,
  Task,
  TaskType,
  Todo,
  TaskList,
  TaskViewType,
  TaskView,
  TaskQueryFilter,
  TaskTag,
} from '@tasks/tasks.types';
import { User } from '@core/models';
import { Contact } from 'app/modules/contacts/contacts.types';

export namespace TaskActions {
  export class SetRelatedTo {
    static readonly type = '[Task] Set Related to Type and Related to Type Id';

    constructor(
      public relatedToType: TaskModuleRelatedToType,
      public relatedToTypeId: number,
      public customObjectName?: string,
    ) {}
  }
  export class SetTaskTemplateId {
    static readonly type = '[Task] Set TaskTemplateId';

    constructor(public taskTemplateId: number) {}
  }

  export class SetViewType {
    static readonly type = '[Task] Set View Type';

    constructor(public viewType: TaskViewType) {}
  }

  export class GetTaskTags {
    static readonly type = '[Task] Get Task Tags';
  }

  export class GetTaskContacts {
    static readonly type = '[Task] Get Task Contacts';
  }

  export class LaunchConfetti {
    static readonly type = '[Task] Launch Confetti';
  }

  export class GetTaskDetail {
    static readonly type = '[Task] Get Task Detail By Id';

    constructor(
      public taskListId: number,
      public taskId: number,
    ) {}
  }

  export class DuplicateTask {
    static readonly type = '[Task] Duplicate Task';

    constructor(
      public taskListId: number,
      public task: Task,
      public templateId: number,
    ) {}
  }

  export class UpdateTask {
    static readonly type = '[Task] Update Task By Id';

    constructor(
      public task: Task,
      public templateId?: number,
      public recalculateTaskList: boolean = true,
    ) {}
  }

  export class UnlinkTask {
    static readonly type = '[Task] Unlink Task';

    constructor(public task: Task) {}
  }

  export class CreateTask {
    static readonly type = '[Task] Create Task';

    constructor(public model: Partial<Task>) {}
  }

  export class CreateTodo {
    static readonly type = '[Task] Create Todo';

    constructor() {}
  }

  export class DeleteTodo {
    static readonly type = '[Task] Delete Todo';

    constructor(
      public task: Task,
      public todo: Todo,
    ) {}
  }
  export class AddApprover {
    static readonly type = '[Task] Add approver';

    constructor(
      public task: Task,
      public approval: Approval,
    ) {}
  }

  export class RemoveApprover {
    static readonly type = '[Task] Remove approver';

    constructor(
      public task: Task,
      public approval: Approval,
    ) {}
  }

  export class AddFollower {
    static readonly type = '[Task] Add User as Follower';

    constructor(
      public task: Task,
      public user: User,
    ) {}
  }

  export class RemoveFollower {
    static readonly type = '[Task] Remover User from follower list';

    constructor(
      public task: Task,
      public user: User,
    ) {}
  }

  export class AddContact {
    static readonly type = '[Task] Add Contact';

    constructor(
      public task: Task,
      public contact: Partial<Contact>,
    ) {}
  }

  export class RemoveContact {
    static readonly type = '[Task] Remove Contact list';

    constructor(
      public task: Task,
      public contact: Partial<Contact>,
    ) {}
  }

  export class CreateSubTask {
    static readonly type = '[Task] Create Sub Task';

    constructor() {}
  }

  export class DeleteTask {
    static readonly type = '[Task] Delete';

    constructor(
      public taskListId: number,
      public taskId: number,
      public templateId?: number,
    ) {}
  }

  export class UpdateSectionTitle {
    static readonly type = '[Task] Update Section Title';

    constructor(public task: Partial<TaskTag>) {}
  }

  export class UpdateTaskView {
    static readonly type = '[Task] Update Task View';

    constructor(public taskView: TaskView) {}
  }

  export class GetSidebarItems {
    static readonly type = '[Task] Get All Sidebar Items';

    constructor() {}
  }

  export class SetSidebarItems {
    static readonly type = '[Task] Set Sidebar Items';

    constructor(public sidebarItems: TaskView[]) {}
  }

  export class CreateTaskList {
    static readonly type = '[Task] Create TaskList';

    constructor(public taskList: TaskList) {}
  }
  export class GetTaskLists {
    static readonly type = '[Task] Get All TaskLists';

    constructor() {}
  }

  export class GetTaskListById {
    static readonly type = '[Task] Get Task List By Id';

    constructor(
      public taskListId: number,
      public templateId: number,
      public includeTasks: boolean,
    ) {}
  }

  export class DeleteTaskList {
    static readonly type = '[Task] Delete Task List By Id';

    constructor(public taskListId: number) {}
  }

  export class SetTaskList {
    static readonly type = '[Task] Update State with Task List';

    constructor(public taskList: TaskList) {}
  }

  export class TaskListStartTrackingDates {
    static readonly type = '[Task] Task List Start Tracking Dates';

    constructor(public taskListId: number) {}
  }

  export class SetTaskView {
    static readonly type = '[Task] current task view';

    constructor(public taskView: TaskView) {}
  }

  export class UndoTaskViewChanges {
    static readonly type = '[Task] Undo Task View Changes';

    constructor() {}
  }

  export class UpdateTaskList {
    static readonly type = '[Task] Update Task List';

    constructor(public taskList: TaskList) {}
  }

  export class ReorderTaskLists {
    static readonly type = '[Task] Reorder Task Lists';

    constructor(public idsToRanks: Record<number, number>) {}
  }

  export class RecalculateTaskListDates {
    static readonly type = '[Task] Recalculate Task List Dates';

    constructor(public taskListId: number) {}
  }

  export class ClearTaskList {
    static readonly type = '[Task] Clear Task list state';

    constructor() {}
  }

  export class SetPage {
    static readonly type = '[Task] Set Current Page';

    constructor(public page: number) {}
  }

  export class SetPageSize {
    static readonly type = '[Task] Set Current Page Size';

    constructor(public pageSize: number) {}
  }

  export class GetTasks {
    static readonly type = '[Task] Get Tasks By Tas List Id';

    constructor(
      public taskListId: number,
      public filters: any,
    ) {}
  }

  export class FilterTasks {
    static readonly type = '[Task] Filter Tasks';

    /**
     * @param showProgressBar When `false`, task shell skips the main-content Jupiter loading overlay (background refresh, export, etc.).
     * Default / `true`: show bar while the request runs.
     */
    constructor(
      public queryFilters: TaskQueryFilter,
      public showProgressBar: boolean | undefined = true,
    ) {}
  }

  export class SyncCascadingChanges {
    static readonly type = '[Task] Sync Cascading Changes';

    constructor(public queryFilters: TaskQueryFilter) {}
  }

  export class LoadMoreTasks {
    static readonly type = '[Task] Load More Tasks';
    constructor(public source: 'scroll' | 'auto' = 'auto') {}
  }

  export class AutoLoadAllTasks {
    static readonly type = '[Task] Auto Load All Tasks';
  }

  export class ResetInfiniteScroll {
    static readonly type = '[Task] Reset Infinite Scroll';
  }

  export class FetchPartialTaskData {
    static readonly type = '[Task] Fetch Partial Task Data';

    constructor(public fields: string[]) {}
  }

  export class LoadTaskPredecessorOptions {
    static readonly type = '[Task] Load Task Predecessor Options';
  }

  /** Marks predecessor picker data stale (e.g. after title change, create/delete task). */
  export class InvalidatePredecessorOptions {
    static readonly type = '[Task] Invalidate Predecessor Options';
  }

  export class SetSelectedTasks {
    static readonly type = '[Task] Set Selected Tasks';

    constructor(public tasks: Task[]) {}
  }

  export class BulkDeleteSelectedTasks {
    static readonly type = '[Task] Bulk Delete Selected Tasks';

    constructor() {}
  }

  export class BulkDuplicateSelectedTasks {
    static readonly type = '[Task] Bulk Duplicate Selected Tasks';

    constructor() {}
  }

  export class BulkUpdateSelectedTasks {
    static readonly type = '[Task] Bulk Update Selected Tasks';

    constructor(public newTaskParams: Partial<Task>) {}
  }

  export class ReorderColumns {
    static readonly type = '[Task] Reorder Columns';

    constructor(public columns: string[]) {}
  }

  export class ReorderTasks {
    static readonly type = '[Task] Reorder Tasks';

    constructor(
      public taskListId: number,
      public tasks: Task[],
      public templateId?: number,
    ) {}
  }

  export class ReorderTaskSection {
    static readonly type = '[Task] Reorder Task Section';

    constructor(
      public taskListId: number,
      public updateData: Partial<Task>,
      public templateId?: number,
      public optimisticTasks?: Task[],
    ) {}
  }

  export class ReorderSubtask {
    static readonly type = '[Task] Reorder Subtask';

    constructor(
      public taskListId: number,
      public subtaskId: number,
      public parentTaskId: number,
      public newRank: number,
    ) {}
  }

  export class ClearFilter {
    static readonly type = '[Task] Clear searched ids';
    constructor() {}
  }

  export class ClearCurrentTask {
    static readonly type = '[Task] Clear Current Task';
  }

  export class SetCurrentTaskPreview {
    static readonly type = '[Task] Set Current Task Preview';
    constructor(public task: Partial<Task>) {}
  }

  export class UpdateCurrentTaskNums {
    static readonly type = '[Task] Update Current Task Nums';
    constructor(
      public taskListId?: number,
      public taskId?: number,
    ) {}
  }

  export class NavigateToTaskList {
    static readonly type = '[Task] Navigate To Task List';

    constructor(
      public taskListId: number,
      public viewType?: string,
    ) {}
  }

  export class GetDraftPreviewChanges {
    static readonly type = '[Task] Get Draft Preview Changes';

    constructor(public taskListId: number) {}
  }

  export class ClearDraftPreviewChanges {
    static readonly type = '[Task] Clear Draft Preview Changes';
  }

  export class PublishDraft {
    static readonly type = '[Task] Publish Draft';
    constructor(public taskListId: number) {}
  }

  export namespace TaskList {
    export class ToggleVisibility {
      static readonly type = '[Task List] Toggle Visibility';

      constructor(public id: number) {}
    }
  }
}
